/*
 * Ella multi-piece package selector.
 * Tags: bundle:<quantity>:<display price>[:<selection count>].
 * A Shopify discount or app must enforce the checkout price.
 */
class PackageBuilder extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    const node = this.querySelector('[data-package-builder-config]');
    if (!node) return;
    try { this.config = JSON.parse(node.textContent); }
    catch (error) { console.warn('Package builder configuration could not be read.', error); return; }

    this.offers = this.parseOffers(this.config.tags || []);
    if (!this.offers.length || !this.config.product?.variants?.length) return;
    this.product = this.config.product;
    this.labels = this.getLabels();
    this.productView = this.closest('.productView') || this.closest('[data-product-view]');
    this.variantArea = this.parentElement;
    this.form = this.productView?.querySelector('form[data-type="add-to-cart-form"]');
    if (!this.form) { console.warn('Package builder could not find the Ella product form.'); return; }

    this.nativeOptions = [...this.variantArea.querySelectorAll('.productView-options')];
    this.activeOffer = this.offers[0];
    this.addEventListener('click', this.onClick.bind(this));
    this.form.addEventListener('submit', this.onSubmit.bind(this), true);
    this.form.addEventListener('click', this.onDynamicCheckout.bind(this), true);
    this.render();
  }

  parseOffers(tags) {
    const unique = new Map();
    tags.forEach((tag) => {
      const match = String(tag).trim().match(/^bundle:(\d+):(\d+(?:\.\d+)?)(?::(\d+))?$/i);
      if (!match) return;
      const offer = { quantity: Number(match[1]), price: Number(match[2]), selectionCount: Number(match[3] || match[1]) };
      if (offer.quantity > 0 && offer.selectionCount > 0 && offer.price >= 0) unique.set(offer.quantity, offer);
    });
    return [...unique.values()].sort((a, b) => a.quantity - b.quantity);
  }

  getLabels() {
    const language = String(this.config.locale || document.documentElement.lang || 'en').toLowerCase().split('-')[0];
    const all = {
      en:{package:'Package',item:'Item',unit:'PCS',packageItem:'Package item',choose:'Please choose an available option for every item.',error:'The package could not be added.'},
      ro:{package:'Pachet',item:'Articol',unit:'BUC.',packageItem:'Articol pachet',choose:'Alegeți o opțiune disponibilă pentru fiecare articol.',error:'Pachetul nu a putut fi adăugat.'},
      bg:{package:'Пакет',item:'Артикул',unit:'БР.',packageItem:'Артикул от пакета',choose:'Изберете налична опция за всеки артикул.',error:'Пакетът не можа да бъде добавен.'},
      es:{package:'Paquete',item:'Artículo',unit:'UDS.',packageItem:'Artículo del paquete',choose:'Elija una opción disponible para cada artículo.',error:'No se pudo añadir el paquete.'},
      fr:{package:'Lot',item:'Article',unit:'PCS',packageItem:'Article du lot',choose:'Choisissez une option disponible pour chaque article.',error:'Impossible d’ajouter le lot.'},
      de:{package:'Paket',item:'Artikel',unit:'STK.',packageItem:'Paketartikel',choose:'Wählen Sie für jeden Artikel eine verfügbare Option.',error:'Das Paket konnte nicht hinzugefügt werden.'},
      it:{package:'Pacchetto',item:'Articolo',unit:'PZ.',packageItem:'Articolo del pacchetto',choose:'Scegli un’opzione disponibile per ogni articolo.',error:'Impossibile aggiungere il pacchetto.'},
      pt:{package:'Pacote',item:'Artigo',unit:'UN.',packageItem:'Artigo do pacote',choose:'Escolha uma opção disponível para cada artigo.',error:'Não foi possível adicionar o pacote.'},
      pl:{package:'Pakiet',item:'Produkt',unit:'SZT.',packageItem:'Produkt w pakiecie',choose:'Wybierz dostępną opcję dla każdego produktu.',error:'Nie udało się dodać pakietu.'},
      hu:{package:'Csomag',item:'Tétel',unit:'DB',packageItem:'Csomag tétel',choose:'Válasszon elérhető opciót minden tételhez.',error:'A csomagot nem sikerült hozzáadni.'},
      zh:{package:'套餐',item:'商品',unit:'件',packageItem:'套餐商品',choose:'请为每件商品选择可售规格。',error:'套餐添加失败。'}
    };
    return all[language] || all.en;
  }

  escape(value) {
    const node = document.createElement('span');
    node.textContent = String(value);
    return node.innerHTML;
  }

  money(value) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style:'currency', currency:this.config.currency || 'USD',
        minimumFractionDigits:0, maximumFractionDigits:0
      }).format(value);
    } catch (_) { return `${value} ${this.config.currency || ''}`.trim(); }
  }

  get startingVariant() {
    return this.product.variants.find((variant) => variant.available) || this.product.variants[0];
  }

  render() {
    this.hidden = false;
    this.innerHTML = `<fieldset class="package-picker">
      <legend class="form__label">${this.labels.package}:</legend>
      <div class="package-picker__offers">${this.offers.map((offer) => `
        <button type="button" class="package-card${offer === this.activeOffer ? ' is-selected' : ''}"
          data-package-quantity="${offer.quantity}" aria-pressed="${offer === this.activeOffer}">
          <span class="package-card__radio" aria-hidden="true"></span>
          <span class="package-card__title">${offer.quantity} ${this.labels.unit}</span>
          <span class="package-card__price">${this.money(offer.price)}</span>
        </button>`).join('')}</div>
      </fieldset><div class="package-builder__components"></div>`;
    this.setMode();
  }

  setMode() {
    const multipack = this.activeOffer.quantity > 1;
    this.nativeOptions.forEach((node) => node.classList.toggle('package-builder__native-options-hidden', multipack));
    this.querySelector('.package-builder__components').hidden = !multipack;
    if (multipack) this.renderComponents();
  }

  renderComponents() {
    const selected = this.startingVariant.options || [];
    this.querySelector('.package-builder__components').innerHTML =
      Array.from({length:this.activeOffer.selectionCount}, (_, itemIndex) => `
        <fieldset class="package-builder__component">
          <legend class="form__label">${this.labels.item} ${itemIndex + 1}</legend>
          ${this.product.options.map((name, optionIndex) => {
            const values = [...new Set(this.product.variants.map((variant) => variant.options[optionIndex]))];
            return `<div class="package-builder__option">
              <span class="package-builder__option-label">${this.escape(name)}:</span>
              <div class="package-builder__values">${values.map((value) => `<button type="button"
                class="package-builder__value${value === selected[optionIndex] ? ' is-selected' : ''}"
                data-value="${this.escape(value)}">${this.escape(value)}</button>`).join('')}</div>
              </div>`;
          }).join('')}
        </fieldset>`).join('');
    this.updateAvailability();
  }

  onClick(event) {
    const offerButton = event.target.closest('[data-package-quantity]');
    if (offerButton) {
      this.activeOffer = this.offers.find((offer) => offer.quantity === Number(offerButton.dataset.packageQuantity));
      this.render();
      return;
    }
    const valueButton = event.target.closest('.package-builder__value');
    if (!valueButton || valueButton.disabled) return;
    valueButton.parentElement.querySelectorAll('.package-builder__value').forEach((button) => button.classList.remove('is-selected'));
    valueButton.classList.add('is-selected');
    this.updateAvailability();
  }

  updateAvailability() {
    this.querySelectorAll('.package-builder__component').forEach((component) => {
      const options = [...component.querySelectorAll('.package-builder__option')];
      const selected = options.map((option) => option.querySelector('.is-selected')?.dataset.value);
      options.forEach((option, optionIndex) => option.querySelectorAll('.package-builder__value').forEach((button) => {
        const possible = this.product.variants.some((variant) =>
          variant.available && variant.options.every((value, index) =>
            index === optionIndex ? value === button.dataset.value : value === selected[index]));
        button.disabled = !possible;
        button.classList.toggle('is-unavailable', !possible);
      }));
    });
  }

  selectedVariants() {
    return [...this.querySelectorAll('.package-builder__component')].map((component) => {
      const selected = [...component.querySelectorAll('.package-builder__option')]
        .map((option) => option.querySelector('.is-selected')?.dataset.value);
      return this.product.variants.find((variant) =>
        variant.available && variant.options.every((value, index) => value === selected[index]));
    });
  }

  onSubmit(event) {
    if (this.activeOffer.quantity === 1) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.addPackage(false);
  }

  onDynamicCheckout(event) {
    if (this.activeOffer.quantity === 1 || !event.target.closest('.shopify-payment-button')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.addPackage(true);
  }

  async addPackage(goToCheckout) {
    if (this.adding) return;
    const variants = this.selectedVariants();
    if (variants.some((variant) => !variant)) { window.alert(this.labels.choose); return; }
    this.adding = true;
    const submit = this.form.querySelector('[type="submit"]');
    submit?.setAttribute('disabled','disabled');
    submit?.classList.add('loading');
    const packageId = `package-${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
    const items = variants.map((variant, index) => ({
      id:variant.id, quantity:1,
      properties:{
        [this.labels.package]:`${this.activeOffer.quantity} ${this.labels.unit}`,
        [this.labels.packageItem]:`${index + 1} / ${this.activeOffer.selectionCount}`,
        _package_id:packageId
      }
    }));
    try {
      const response = await fetch(this.config.cartAddUrl || window.routes?.cart_add_url || '/cart/add.js', {
        method:'POST', headers:{'Content-Type':'application/json',Accept:'application/json'},
        body:JSON.stringify({items})
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || this.labels.error);
      window.location.href = goToCheckout ? '/checkout' : (this.config.cartUrl || window.routes?.cart_url || '/cart');
    } catch (error) {
      console.error(error);
      window.alert(error.message || this.labels.error);
      this.adding = false;
      submit?.removeAttribute('disabled');
      submit?.classList.remove('loading');
    }
  }
}
if (!customElements.get('package-builder')) customElements.define('package-builder', PackageBuilder);
